import { type RefObject, useEffect, useRef } from 'react';
import { leaderboardManager } from '@/app_lib/services/ads/leaderboardManager';

const GAM_NETWORK_ID = process.env.GAM_ID || '21809533738';
const GAM_NAME = process.env.GAM_NAME || 'fortune';
const LEADERBOARD_SIZES: [number, number][] = [
  [970, 90],
  [970, 250],
  [728, 90],
  [300, 50],
  [320, 50],
];
const FALLBACK_SLOT_ID = 'gam-direct-lb';
const FALLBACK_DELAY_MS = 1500;

function applySlotTargeting(
  // biome-ignore lint/suspicious/noExplicitAny: GPT slot type varies
  slot: any,
  ch: string,
  ctype: string | undefined,
) {
  slot.setTargeting('ch', ch);
  slot.setTargeting('placement_name', 'Leaderboard');
  slot.setTargeting('pos', 'atf');
  slot.setTargeting('index', '1');
  if (ctype) {
    slot.setTargeting('ctype', ctype);
  }
}

/**
 * Path A: Freestar registered a slot but didn't refresh -- add targeting and refresh.
 * Returns true if handled.
 */
function tryRefreshExistingSlot(
  // biome-ignore lint/suspicious/noExplicitAny: GPT API types
  gt: any,
  ch: string,
  ctype: string | undefined,
): boolean {
  const existingSlots = gt.pubads().getSlots();
  // biome-ignore lint/suspicious/noExplicitAny: GPT slot type
  const freestarSlot = existingSlots.find((s: any) => s.getAdUnitPath().includes('leaderboard_top'));
  if (!freestarSlot) {
    return false;
  }

  const slotEl = document.getElementById('Leaderboard0');
  const hasAd = slotEl?.querySelector('iframe[id^="google_ads"]');
  if (hasAd && (hasAd as HTMLIFrameElement).clientHeight > 0) {
    return true;
  }

  applySlotTargeting(freestarSlot, ch, ctype);
  gt.pubads().setPrivacySettings({ nonPersonalizedAds: false });
  gt.pubads().refresh([freestarSlot]);
  leaderboardManager();
  return true;
}

/**
 * Path B: No slot exists -- create a fallback div, define the slot via GPT directly,
 * and call leaderboardManager for proper sizing.
 */
function createFallbackSlot(
  // biome-ignore lint/suspicious/noExplicitAny: GPT API types
  gt: any,
  ch: string,
  ctype: string | undefined,
  container: HTMLDivElement,
) {
  if (document.getElementById(FALLBACK_SLOT_ID)) {
    return;
  }

  const div = document.createElement('div');
  div.id = FALLBACK_SLOT_ID;
  div.className = 'leaderboard-ad-wrapper';
  container.appendChild(div);

  const adUnitPath = `/${GAM_NETWORK_ID}/${GAM_NAME}/leaderboard_top`;
  const slot = gt.defineSlot(adUnitPath, LEADERBOARD_SIZES, FALLBACK_SLOT_ID);
  if (!slot) {
    return;
  }

  slot.addService(gt.pubads());
  applySlotTargeting(slot, ch, ctype);
  gt.pubads().setPrivacySettings({ nonPersonalizedAds: false });
  gt.display(FALLBACK_SLOT_ID);
  gt.pubads().refresh([slot]);
  leaderboardManager();
}

/**
 * When Freestar suppresses ad slot creation in reject state, direct-sold GAM
 * campaigns (Sponsorships, Roadblocks) never serve. This fallback creates
 * the leaderboard slot directly via GPT with the required targeting so
 * direct-sold line items can still match and serve.
 */
export function useDirectSoldFallback(
  channel: string | undefined,
  ctype: string | undefined,
  containerRef: RefObject<HTMLDivElement | null>,
) {
  const fallbackFired = useRef(false);

  useEffect(() => {
    if (!channel) {
      return;
    }
    if (typeof window === 'undefined' || !window.googletag) {
      return;
    }
    if (fallbackFired.current) {
      return;
    }

    const timer = setTimeout(() => {
      if (fallbackFired.current) {
        return;
      }

      const gt = window.googletag;
      const ch = channel === 'homepage' ? 'home' : channel;

      if (tryRefreshExistingSlot(gt, ch, ctype)) {
        fallbackFired.current = true;
        return;
      }

      const container = containerRef.current;
      if (!container) {
        return;
      }

      fallbackFired.current = true;
      createFallbackSlot(gt, ch, ctype, container);
    }, FALLBACK_DELAY_MS);

    return () => clearTimeout(timer);
  }, [channel, ctype, containerRef]);
}
